<template>
  <div class="survey-info-container">
    <router-link to="/" class="back-to-survey-btn">
      <i class="fas fa-arrow-left"></i> Back to Survey
    </router-link>
    <div class="info-header">
      <h1>About the Cyber Security Survey</h1>
      <p class="subtitle">
        Learn what to expect, how your data is handled, and how to get help.
      </p>
    </div>

    <section class="info-section">
      <h2><i class="fas fa-list-ol section-icon"></i> Survey Steps</h2>
      <ol class="steps-list">
        <li>
          <strong>1.</strong> <strong>Contact Information:</strong> Enter your
          email and basic details. You'll receive a secure sign-in link.
        </li>
        <li>
          <strong>2.</strong>
          <strong>Location & Work Arrangements:</strong> Tell us about your work
          location and setup.
        </li>
        <li>
          <strong>3.</strong>
          <strong>Professional Competencies:</strong> Self-assess your
          cybersecurity skills in an interactive way.
        </li>
        <li>
          <strong>4.</strong> <strong>Professional Experience:</strong> Share
          your role, experience, and industry background.
        </li>
        <li>
          <strong>5.</strong> <strong>Career Pathways:</strong> Let us know
          about your upskilling and certifications.
        </li>
        <li>
          <strong>6.</strong> <strong>Demographics:</strong> Provide general
          demographic info (age range, etc.).
        </li>
        <li>
          <strong>7.</strong> <strong>Thank You:</strong> See confirmation that
          your responses are saved.
        </li>
      </ol>
    </section>

    <section class="info-section">
      <h2>
        <i class="fas fa-shield-alt section-icon"></i> Data Privacy & Consent
      </h2>
      <ul class="privacy-list">
        <li>
          Your data is <strong>securely stored</strong> and only used for
          research and reporting.
        </li>
        <li>
          You must <strong>give consent</strong> before starting. Consent can be
          withdrawn at any time.
        </li>
        <li>
          Progress is saved automatically, so you can return and continue later.
        </li>
        <li>
          After completion, your progress data is cleared from your device.
        </li>
        <li>
          Survey data is retained for 2 years, progress data for 30 days, and
          consent records for 5 years.
        </li>
      </ul>
    </section>

    <section class="info-section">
      <h2><i class="fas fa-route section-icon"></i> How the Survey Works</h2>
      <ul class="flow-list">
        <li>Give consent to begin.</li>
        <li>
          Complete each step at your own pace. Your progress is saved
          automatically.
        </li>
        <li>If you leave, you can return and pick up where you left off.</li>
        <li>
          Once finished, you'll see a thank you message and your responses are
          securely recorded.
        </li>
      </ul>
    </section>

    <section class="info-section">
      <h2><i class="fas fa-question-circle section-icon"></i> Need Help?</h2>
      <p>
        If you have questions about the survey or your data, please contact
        <a href="mailto:privacy@cyberireland.ie">privacy@cyberireland.ie</a>.
      </p>
    </section>
  </div>
</template>

<script setup>
// No script logic needed for static info page
</script>

<style scoped>
.survey-info-container {
  max-width: 1000px;
  margin: 2rem auto;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 32px rgba(0, 84, 97, 0.08);
  padding: 2.5rem 2rem 2rem 2rem;
  font-family: "Segoe UI", "Roboto", Arial, sans-serif;
  color: #1a232b;
  position: relative;
}

.back-to-survey-btn {
  position: absolute;
  top: 1.5rem;
  right: 2rem;
  background: #005461;
  color: #fff;
  padding: 0.6rem 1.3rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 84, 97, 0.08);
  transition: background 0.2s, color 0.2s;
  z-index: 10;
}
.back-to-survey-btn:hover {
  background: #003d49;
  color: #fff;
}

.info-header {
  text-align: left;
  margin-bottom: 2.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

h1 {
  text-align: left;
}

.subtitle {
  color: #4a5a6a;
  font-size: 1.1rem;
  margin-top: 0.5rem;
}

.info-section {
  margin-bottom: 2.2rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid #e3e8ee;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
}

.info-section:last-child {
  border-bottom: none;
}

.info-section h2 {
  text-align: left;
}

.section-icon {
  color: #005461;
  margin-right: 0.5rem;
}

.steps-list,
.privacy-list,
.flow-list {
  margin: 1rem 0 0 1.5rem;
  padding: 0;
  font-size: 1.05rem;
}

.steps-list li,
.privacy-list li,
.flow-list li {
  margin-bottom: 0.7rem;
  line-height: 1.6;
}

strong {
  color: #005461;
  font-weight: 700;
}

a {
  color: #005461;
  text-decoration: underline;
}

a:hover {
  color: #003d49;
}

@media (max-width: 800px) {
  .survey-info-container {
    padding: 1.2rem 0.5rem;
  }
}
</style>
